import { yupResolver } from '@hookform/resolvers/yup';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import Typography from '@mui/material/Typography';
import { useQueries } from '@tanstack/react-query';
import {
  BulkDownload_Create,
  BulkDownloadSettlementPdfCategory,
  BulkDownloadType,
  FileAttachmentCategory,
  getV1CompaniesCompanyIdDriversId,
  postV1BulkDownloads,
  PostV1BulkDownloadsData,
} from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { get, pick, snakeCase } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import { CheckBoxFormField } from '~components/FormFields/CheckBoxFormField';
import { CheckBoxGroupFormField } from '~components/FormFields/CheckBoxGroupFormField';
import { RadioGroupFormField } from '~components/FormFields/RadioGroupFormField';
import SingleDateFormField from '~components/FormFields/SingleDateFormField';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import AppContext from '~contexts/FileDownloadResultContext';
import { Driver } from '~hooks/useDrivers/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import type { FilesQueryFilters } from '../../FilesQueryContext';
import { getEffectiveJobStates, omitWhenEmpty } from '../../utils';

const MAX_DRIVER_NAMES_DISPLAYED = 5;
const DRIVER_SPINNER_MIN_VISIBLE_MS = 1000;

type PageFilter = Pick<
  FilesQueryFilters,
  | 'customerAccountIds'
  | 'dispatchNumberIds'
  | 'driverIds'
  | 'dropoffSiteIds'
  | 'endDate'
  | 'foremanIds'
  | 'jobStates'
  | 'pickupSiteIds'
  | 'projectIds'
  | 'startDate'
  | 'vendorAccountIds'
>;

type BulkDownloadDialogState = {
  isReadyToSubmit: boolean;
  isSubmitting: boolean;
};

type DownloadFilesDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  preselectTimesheet?: boolean;
} & (
  | { jobIds: string[]; pageFilter?: never; settlementIds?: never }
  | { jobIds?: never; pageFilter: PageFilter; settlementIds?: never }
  | { jobIds?: never; pageFilter?: never; settlementIds: string[] }
);

export default function DownloadFilesDialog({
  isOpen,
  jobIds,
  pageFilter,
  settlementIds,
  onClose,
  preselectTimesheet = false,
}: DownloadFilesDialogProps) {
  if (isOpen && !jobIds?.length && !settlementIds?.length && !pageFilter) {
    throw new Error('"jobIds", "settlementIds" or "pageFilter" must be provided');
  }

  const { toasterStore } = useStores();
  const { enqueueFileDownload } = useContext(AppContext);

  const formHandler = useRef<DownloadFilesFormHandler>(null);
  const [dialogState, setDialogState] = useState<BulkDownloadDialogState>({
    isReadyToSubmit: false,
    isSubmitting: false,
  });

  const handleSubmissionReadyStateChange = useCallback((isReadyToSubmit: boolean) => {
    setDialogState((state) => ({ ...state, isReadyToSubmit }));
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setDialogState({ isReadyToSubmit: true, isSubmitting: true });

      const formData = await formHandler.current?.onSubmit();

      if (!formData) {
        throw new Error();
      }

      const dispatchNumberIds = omitWhenEmpty(pageFilter?.dispatchNumberIds);
      const body: PostV1BulkDownloadsData['body'] = pageFilter
        ? {
            type: formData.format,
            filter: {
              categories: formData.fileCategories,
              job: {
                start_date: formData.startDate
                  ? dayjs.tz(formData.startDate).startOf('day').toISOString()
                  : undefined,
                end_date: formData.endDate
                  ? dayjs.tz(formData.endDate).endOf('day').toISOString()
                  : undefined,
                customer_account_ids: omitWhenEmpty(pageFilter.customerAccountIds),
                driver_ids: omitWhenEmpty(pageFilter.driverIds),
                dropoff_site_ids: omitWhenEmpty(pageFilter.dropoffSiteIds),
                foreman_ids: omitWhenEmpty(pageFilter.foremanIds),
                order: dispatchNumberIds
                  ? { dispatch_number_ids: dispatchNumberIds }
                  : undefined,
                pickup_site_ids: omitWhenEmpty(pageFilter.pickupSiteIds),
                project_ids: omitWhenEmpty(pageFilter.projectIds),
                states: getEffectiveJobStates(pageFilter.jobStates),
                vendor_account_ids: omitWhenEmpty(pageFilter.vendorAccountIds),
              },
            },
          }
        : settlementIds
          ? {
              settlement_ids: settlementIds,
              type: formData.format,
              filter: { categories: formData.fileCategories },
              ...(formData.format === BulkDownloadType.PDF
                ? { merge_files: formData.mergeFiles }
                : {}),
            }
          : {
              job_ids: jobIds,
              type: formData.format,
              filter: { categories: formData.fileCategories },
            };

      const response = await postV1BulkDownloads({ body });

      setDialogState({ isSubmitting: false, isReadyToSubmit: false });
      enqueueFileDownload(response.data.data.id);
      onClose();
    } catch (error) {
      setDialogState({ isSubmitting: false, isReadyToSubmit: false });

      if (isAxiosError(error)) {
        const errorObject = get(error, 'response.data.error.errors.0', { message: '' });
        const errorMessage = String(errorObject.message).toLowerCase();
        const isNoRecordsError = errorMessage === 'no records found';
        const isTooManyRecordsError = errorMessage.startsWith('too many records found');

        if (isNoRecordsError) {
          toasterStore.push(
            alert(
              t('approvals.files.download_files_dialog.no_records_found_error_message'),
              AlertTypes.info,
            ),
          );

          return;
        } else if (isTooManyRecordsError) {
          toasterStore.push(
            alert(
              t('approvals.files.download_files_dialog.too_many_records_error_message'),
              AlertTypes.info,
            ),
          );

          return;
        }
      }

      connection.handleRequestError(
        error,
        t('approvals.files.download_files_dialog.failed_to_download_files'),
      );
    }
  }, [jobIds, pageFilter, settlementIds, onClose]);

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <DialogHeader onClose={onClose}>
        {t('approvals.files.download_files_dialog.title')}
      </DialogHeader>

      <DialogContent>
        <DownloadFilesForm
          ref={formHandler}
          isSettlementDownload={Boolean(settlementIds)}
          onSubmissionReadyStateChange={handleSubmissionReadyStateChange}
          pageFilter={pageFilter}
          preselectTimesheet={preselectTimesheet}
        />
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={dialogState.isSubmitting}
          onClick={onClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={!dialogState.isReadyToSubmit}
          loading={dialogState.isSubmitting}
          onClick={handleSubmit}
        >
          {t('approvals.files.download_files_dialog.download_cta')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}

type BulkDownloadFileCategory = NonNullable<
  NonNullable<BulkDownload_Create['filter']>['categories']
>[number];

const SETTLEMENT_PDF_CATEGORY = BulkDownloadSettlementPdfCategory.SETTLEMENT_PDF;

const primaryFileCategories: BulkDownloadFileCategory[] = [
  FileAttachmentCategory.SCALE_TICKET_INTERNAL,
  FileAttachmentCategory.TIMESHEET,
];

const settlementPrimaryFileCategories: BulkDownloadFileCategory[] = [
  SETTLEMENT_PDF_CATEGORY,
  ...primaryFileCategories,
];

const advancedFileCategories = [
  FileAttachmentCategory.SCALE_TICKET_EXTERNAL,
  FileAttachmentCategory.INSPECTION_REPORT,
  FileAttachmentCategory.SITE_MAP,
  FileAttachmentCategory.OTHER,
];

const availableFileCategories: BulkDownloadFileCategory[] = [
  SETTLEMENT_PDF_CATEGORY,
  ...primaryFileCategories,
  ...advancedFileCategories,
];

const downloadFilesSchema = yup.object().shape({
  fileCategories: yup
    .array()
    .of(yup.mixed<BulkDownloadFileCategory>().oneOf(availableFileCategories).required())
    .min(1)
    .required(),
  format: yup
    .mixed<BulkDownload_Create['type']>()
    .oneOf(Object.values(BulkDownloadType))
    .required(),
  mergeFiles: yup.boolean().required(),
  startDate: yup.string().optional(),
  endDate: yup.string().optional(),
});

type DownloadFilesDto = yup.InferType<typeof downloadFilesSchema>;

type DownloadFilesFormSubmitData = Pick<
  DownloadFilesDto,
  'fileCategories' | 'format' | 'mergeFiles' | 'startDate' | 'endDate'
>;

type DownloadFilesFormHandler = {
  onSubmit: () => Promise<DownloadFilesFormSubmitData>;
};

interface DownloadFilesFormProps {
  isSettlementDownload: boolean;
  onSubmissionReadyStateChange: (isReady: boolean) => void;
  pageFilter?: PageFilter;
  preselectTimesheet: boolean;
}

const DownloadFilesForm = forwardRef<DownloadFilesFormHandler, DownloadFilesFormProps>(
  function DownloadFilesForm(
    {
      isSettlementDownload,
      onSubmissionReadyStateChange,
      pageFilter,
      preselectTimesheet,
    },
    ref,
  ) {
    const form = useForm<DownloadFilesDto>({
      resolver: yupResolver(downloadFilesSchema),
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        fileCategories: getDefaultFileCategories(
          isSettlementDownload,
          preselectTimesheet,
        ),
        format: '' as BulkDownload_Create['type'],
        mergeFiles: true,
        startDate: pageFilter?.startDate?.format('YYYY-MM-DD'),
        endDate: pageFilter?.endDate?.format('YYYY-MM-DD'),
      },
    });

    const watchedFormat = form.watch('format');
    const watchedStartDate = form.watch('startDate');
    const watchedEndDate = form.watch('endDate');

    const hasFileCategoriesChecked = Boolean(form.watch('fileCategories').length);
    const hasFormatChecked = Boolean(form.watch('format'));
    const hasRequiredDates = !pageFilter || (watchedStartDate && watchedEndDate);
    const isReadyToSubmit =
      hasFileCategoriesChecked && hasFormatChecked && Boolean(hasRequiredDates);

    useEffect(() => {
      onSubmissionReadyStateChange(isReadyToSubmit);
    }, [onSubmissionReadyStateChange, isReadyToSubmit]);

    useImperativeHandle(
      ref,
      () => ({
        onSubmit() {
          return new Promise<DownloadFilesFormSubmitData>((resolve) => {
            form.handleSubmit((data) => {
              resolve(
                pick(data, [
                  'fileCategories',
                  'format',
                  'mergeFiles',
                  'startDate',
                  'endDate',
                ]),
              );
            })();
          });
        },
      }),
      [isReadyToSubmit, form.handleSubmit],
    );

    const { userStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    const driverIds = pageFilter?.driverIds ?? [];

    const driverQueries = useQueries({
      queries: driverIds.map((driverId) => ({
        queryKey: ['DownloadFilesDialog_driver', companyId, driverId],
        queryFn: async ({ signal }) => {
          const response = await getV1CompaniesCompanyIdDriversId({
            path: { 'company-id': companyId, id: driverId },
            signal,
          });
          return Driver.parse(response.data.data);
        },
        staleTime: 5 * 60 * 1000,
      })),
    });

    const isLoadingDriverNames =
      driverIds.length > 0 && driverQueries.some((q) => q.isPending);

    // Hold the spinner for a minimum window so it doesn't flash when the
    // queries resolve quickly (e.g., react-query cache hits).
    const [minSpinnerElapsed, setMinSpinnerElapsed] = useState(false);
    useEffect(() => {
      const timer = setTimeout(
        () => setMinSpinnerElapsed(true),
        DRIVER_SPINNER_MIN_VISIBLE_MS,
      );
      return () => clearTimeout(timer);
    }, []);
    const showDriverSpinner =
      driverIds.length > 0 && (isLoadingDriverNames || !minSpinnerElapsed);

    const driverDisplay = useMemo(() => {
      if (driverIds.length === 0 || isLoadingDriverNames) {
        return null;
      }

      const allSucceeded =
        driverQueries.length > 0 && driverQueries.every((q) => q.isSuccess);

      if (allSucceeded) {
        const names = driverQueries.map((q) => q.data!.fullName);
        if (names.length <= MAX_DRIVER_NAMES_DISPLAYED) {
          return names.join(', ');
        }
        const visible = names.slice(0, MAX_DRIVER_NAMES_DISPLAYED).join(', ');
        const overflow = names.length - MAX_DRIVER_NAMES_DISPLAYED;
        return t('approvals.files.download_files_dialog.driver_names_overflow', {
          visible,
          overflow,
        });
      }

      // At least one query failed: fall back to count.
      return t('approvals.files.download_files_dialog.driver_count', {
        count: driverIds.length,
      });
    }, [driverQueries, driverIds.length, isLoadingDriverNames]);

    return (
      <>
        {pageFilter && (
          <>
            <Typography sx={sectionLabelSx}>
              {t('approvals.files.download_files_dialog.date_range')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SingleDateFormField
                control={form.control}
                errors={form.formState.errors}
                name="startDate"
              />
              <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                {t('approvals.files.download_files_dialog.date_range_separator')}
              </Typography>
              <SingleDateFormField
                control={form.control}
                errors={form.formState.errors}
                name="endDate"
              />
            </Box>

            {driverIds.length > 0 && (
              <>
                <Typography sx={sectionLabelSx}>
                  {t('approvals.files.download_files_dialog.drivers')}
                </Typography>
                <Box sx={{ position: 'relative', mb: 2, minHeight: '1.2em' }}>
                  <Fade in={showDriverSpinner} timeout={300} unmountOnExit>
                    <Box sx={{ position: 'absolute', top: 0, left: 0 }}>
                      <CircularProgress size={14} />
                    </Box>
                  </Fade>
                  <Fade in={!showDriverSpinner} timeout={400} unmountOnExit>
                    <Typography
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        fontSize: '12px',
                      }}
                    >
                      {driverDisplay}
                    </Typography>
                  </Fade>
                </Box>
              </>
            )}

            <Divider sx={{ mb: 2 }} />
          </>
        )}

        <Typography sx={sectionLabelSx}>
          {t('approvals.files.download_files_dialog.file_categories')}
        </Typography>

        <CheckBoxGroupFormField
          control={form.control}
          errors={form.formState.errors}
          name="fileCategories"
          options={(isSettlementDownload
            ? settlementPrimaryFileCategories
            : primaryFileCategories
          ).map((category) => ({
            label: t(`enums.file_attachment_category.${snakeCase(category)}`),
            value: category,
          }))}
        />

        <Accordion
          disableGutters
          elevation={0}
          sx={{
            '&:before': { display: 'none' },
            backgroundColor: 'transparent',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              px: 0,
              minHeight: 'auto',
              '& .MuiAccordionSummary-content': { my: 0.5 },
            }}
          >
            <Typography sx={sectionLabelSx}>
              {t('approvals.files.download_files_dialog.advanced')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0, pt: 0 }}>
            <CheckBoxGroupFormField
              control={form.control}
              errors={form.formState.errors}
              name="fileCategories"
              options={advancedFileCategories.map((category) => ({
                label: t(`enums.file_attachment_category.${snakeCase(category)}`),
                value: category,
              }))}
            />
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3 }} />

        <Typography sx={sectionLabelSx}>
          {t('approvals.files.download_files_dialog.select_format')}
        </Typography>

        <RadioGroupFormField
          control={form.control}
          errors={form.formState.errors}
          name="format"
          list={[
            {
              label: t('enums.bulk_download_dreate_type.pdf'),
              value: BulkDownloadType.PDF,
            },
            {
              label: t('enums.bulk_download_dreate_type.zip'),
              value: BulkDownloadType.ZIP,
            },
          ]}
          sx={{ mt: 1 }}
          radioGroupSx={{ display: 'flex', flexDirection: 'column' }}
        />

        {isSettlementDownload && watchedFormat === BulkDownloadType.PDF && (
          <CheckBoxFormField
            control={form.control}
            errors={form.formState.errors}
            label={t('approvals.files.download_files_dialog.merge_files_per_settlement')}
            name="mergeFiles"
          />
        )}
      </>
    );
  },
);

function getDefaultFileCategories(
  isSettlementDownload: boolean,
  preselectTimesheet: boolean,
): BulkDownloadFileCategory[] {
  const scaleTicketCategories: BulkDownloadFileCategory[] = preselectTimesheet
    ? primaryFileCategories
    : [FileAttachmentCategory.SCALE_TICKET_INTERNAL];

  return isSettlementDownload
    ? [SETTLEMENT_PDF_CATEGORY, ...scaleTicketCategories]
    : scaleTicketCategories;
}

const sectionLabelSx = {
  color: (theme: { brandV2: { colors: { treadBlack: string } } }) =>
    theme.brandV2.colors.treadBlack,
  fontSize: '12px',
  fontWeight: 600,
};
